import { sendPageView } from "lib/analytics";
import { useEffect } from "react";
import { useLocation } from "react-router-dom";

/**
 * 振り分けルート。認証状態や feature flag が解決するまで
 * どのページを表示するか決まらないため、hook からは送信しない。
 *
 * `/` は未認証 + flag ON なら LP、認証済みならダッシュボードを返す。
 * `/lp` と `/lp/delivery` も解決するまで FullScreenLoading を返すので同じ扱いにする。
 * 送信責任は RootRoute / LpRoute / LpDeliveryRoute が持つ（useLogicalPageView）。
 */
const DISPATCH_ROUTES: readonly string[] = ["/", "/lp", "/lp/delivery"];

/**
 * 末尾のスラッシュを落とす（`/` はそのまま）。
 *
 * React Router は `/lp/delivery/` も `/lp/delivery` のルートに当てるので、
 * 振り分けルートの判定も同じに揃える。揃えないと、汎用フックが `/lp/delivery/` を送り、
 * 解決後にルート部品が `/lp/delivery` を送って page_view が 2 回になる。
 * @param {string} pathname 実 URL のパス
 * @return {string} 末尾のスラッシュを落としたパス
 */
function stripTrailingSlash(pathname: string): string {
  const stripped = pathname.replace(/\/+$/, "");
  return stripped === "" ? "/" : stripped;
}

/**
 * ルート遷移ごとに GA4 へ page_view を送るフック。
 *
 * SPA では gtag の自動計測が初回ロードしか拾えないため、Router 配下で一度だけ呼ぶ。
 * 振り分けルートは送らない（表示するページが決まっていないため）。
 * 測定 ID 未設定の環境では sendPageView 側で no-op になる。
 * @return {void}
 */
export function useAnalyticsPageView(): void {
  const { pathname } = useLocation();

  useEffect(() => {
    if (DISPATCH_ROUTES.includes(stripTrailingSlash(pathname))) {
      return;
    }
    sendPageView(pathname);
  }, [pathname]);
}

/**
 * 振り分けが解決したあとに、論理パスで page_view を送るフック。
 *
 * 実 URL は変えずに GA4 上のパスだけを分けるために使う。
 * 早期 return より前に呼べるよう、まだ送ってはいけない状態は null で表す。
 * 送信の単位は「直前に送った論理パスと違うとき」なので、
 * 再レンダリングや再マウントで重複して送られることはない。
 * @param {string | null} logicalPath 送る論理パス。まだ送らない場合は null
 * @return {void}
 */
export function useLogicalPageView(logicalPath: string | null): void {
  useEffect(() => {
    if (logicalPath === null) {
      return;
    }
    sendPageView(logicalPath);
  }, [logicalPath]);
}
